import type { CSSProperties } from 'react';
import type { JourneeDuJour as Donnees, TacheDuJour } from '../../partage/api/types';
import { useCocher } from '../journeeDuJour';
import { dureeLisible, heureLisible } from './datesDeLAgenda';
import { teinteDuProjet } from '../../apparence';
import './JourneeDuJour.css';

/**
 * « Ma journée », en tête de « Maintenant » (ticket 16 de retours-testeurs, d'après les écrans
 * « Accueil » et « Ma journée » d'une maquette du téléphone, adaptés à l'ordinateur).
 *
 * Sans journée : une invitation et « Planifier ma journée ». Avec : ses tâches dans l'ordre, à
 * leur heure ; le rond coche (le même « C'est fait » que partout) et dit ce que ça libère ; le
 * titre ouvre la fiche ; « Changer ma journée » rouvre le panneau.
 */
export function JourneeDuJour({
  donnees,
  onPlanifier,
  onOuvrirLaFiche,
  onAnnonce,
}: {
  donnees: Donnees;
  onPlanifier: () => void;
  onOuvrirLaFiche: (t: { bulleId: string; parentId: string | null }) => void;
  onAnnonce: (texte: string) => void;
}) {
  const cocher = useCocher();
  const journee = donnees.journee;

  if (!journee || journee.taches.length === 0) {
    return (
      <section className="journee journee--vide" aria-label="Ma journée" data-tuto="journee">
        <div>
          <p className="journee-question">Tu as du temps pour tes projets aujourd’hui&nbsp;?</p>
          <p className="journee-sous">Dis combien, choisis quoi faire, range-le dans ta journée.</p>
        </div>
        <button type="button" className="journee-fort" onClick={onPlanifier}>
          Planifier ma journée
        </button>
      </section>
    );
  }

  const faites = journee.taches.filter((t) => t.etat === 'fait').length;
  const minutes = journee.taches.reduce((n, t) => n + t.dureeMin, 0);

  function basculer(t: TacheDuJour) {
    const fait = t.etat !== 'fait';
    cocher.mutate(
      { id: t.bulleId, fait },
      {
        onSuccess: () => onAnnonce(fait ? ceQueCaLibere(t) : `« ${t.titre} » est remise à faire.`),
        onError: () => onAnnonce('Ça n’a pas marché : rien n’a changé.'),
      }
    );
  }

  return (
    <section className="journee" aria-label="Ma journée" data-tuto="journee">
      <header className="journee-tete">
        <div>
          <h2>Ma journée</h2>
          <p className="journee-sous">
            {faites} sur {journee.taches.length} faites · {dureeLisible(minutes)} prévues
          </p>
        </div>
        <button type="button" className="journee-doux" onClick={onPlanifier}>
          Changer ma journée
        </button>
      </header>
      <ol className="journee-taches">
        {journee.taches.map((t) => {
          const fait = t.etat === 'fait';
          return (
            <li
              key={t.bulleId}
              className={`journee-tache${fait ? ' est-faite' : ''}`}
              style={{ ['--teinte' as string]: teinteDuProjet(t.dossier?.couleur) } as CSSProperties}
            >
              <span className="journee-heure">{heureLisible(t.heure)}</span>
              <button
                type="button"
                className="journee-rond"
                data-tuto="journee-rond"
                data-journee-rond={t.bulleId}
                aria-pressed={fait}
                aria-label={fait ? `Remettre à faire ${t.titre}` : `C’est fait : ${t.titre}`}
                disabled={cocher.isPending}
                onClick={() => basculer(t)}
              >
                {fait ? '✓' : ''}
              </button>
              <button type="button" className="journee-titre" onClick={() => onOuvrirLaFiche(t)}>
                <span>{t.titre}</span>
                <small>
                  <i className="journee-dossier" aria-hidden="true" />
                  {[t.dossier?.titre, dureeLisible(t.dureeMin)].filter(Boolean).join(' · ')}
                </small>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** « Fait. Ça libère « Réserver le train ». », nommément, comme le débloquant. */
export function ceQueCaLibere(t: TacheDuJour): string {
  if (t.libere.length === 0) return `Fait : « ${t.titre} ».`;
  const noms = t.libere.slice(0, 3).map((n) => `« ${n} »`);
  const reste = t.libere.length - noms.length;
  return `Fait. Ça libère ${noms.join(', ')}${reste > 0 ? ` et ${reste} autre${reste > 1 ? 's' : ''}` : ''}.`;
}
